/**
 * TurnTracker — submit-time binding for chat-turn attribution.
 *
 * A turn's identity is bound once, at submit, into a single object. Every
 * downstream consumer (chat_stream_complete telemetry, message stats maps,
 * persistence metadata) reads from the completed turn — never from ambient
 * refs at completion time.
 *
 * Why: resumed/replayed streams fire onFinish with NO submit. Completion
 * handlers that read ambient refs see a zero start time and stale ids,
 * producing phantom telemetry with zeroed measurements attributed to the
 * wrong turn (2026-06-11 incident). Here, no active turn → complete()
 * returns null and callers skip turn-scoped work.
 */

import type { ChatMode } from '@/lib/ai/prompts/routing'

export interface TurnContext {
  sessionId: string
  chatAttemptId: string
  assistantMessageId: string
  userMessageId: string
  userText: string
  /** Workspace the turn was sent from; persisted as message_metadata.mode. */
  mode?: ChatMode
  startedAt: number
}

export interface CompletedTurn extends TurnContext {
  elapsedMs: number
}

export class TurnTracker {
  private turn: TurnContext | null = null

  get current(): TurnContext | null {
    return this.turn
  }

  /** Bind a new turn at submit. Replaces any abandoned (errored) turn. */
  begin(ids: Omit<TurnContext, 'startedAt'>): TurnContext {
    this.turn = { ...ids, startedAt: Date.now() }
    return this.turn
  }

  /** Close the active turn and return it with elapsedMs; null when there
   *  is no active turn (resumed/replayed stream — skip turn-scoped work). */
  complete(): CompletedTurn | null {
    if (!this.turn) return null
    const done = { ...this.turn, elapsedMs: Date.now() - this.turn.startedAt }
    this.turn = null
    return done
  }
}
